import {AfterViewInit, Component, Input, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from 'rxjs';
import {finalize, map} from 'rxjs/operators';
import {AlarmService} from '../../../../core/alarm/services/alarm.service';

const ALARM_SERIES_COLORS = ['#ef535d', '#f59e0b', '#60a5fa', '#22c55e', '#a78bfa', '#f97316', '#e879f9', '#14b8a6'];

@Component({
  selector: 'app-zone-step-line',
  templateUrl: './zone-step-line.component.html',
  styleUrls: ['./zone-step-line.component.scss']
})
export class ZoneStepLineComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() locationId: number;
  @Input() compact = false;
  @Input() compactHeight = 150;

  private _alarmList: any[] | null = null;
  private subscription?: Subscription;

  alarmChartData: any[] = [];
  alarmChartColors: any[] = [];
  alarmChartOptions: any = {};
  alarmChartLegend = true;
  alarmChartType = 'line';
  alarmChartReady = false;
  alarmChartTypes: string[] = [];
  loaded = false;
  loading = false;
  error = false;

  get alarmList(): any[] | null {
    return this._alarmList;
  }

  @Input() set alarmList(alarmList: any[] | null) {
    this._alarmList = alarmList === null || alarmList === undefined ? null : [...alarmList].sort((a, b) => {
      return (this.getAlarmDate(a)?.getTime() || 0) - (this.getAlarmDate(b)?.getTime() || 0);
    });
    if (this._alarmList !== null) this.updateAlarmList();
  }

  constructor(private alarmService: AlarmService) { }

  ngOnInit(): void { }

  ngAfterViewInit(): void {
    if (this._alarmList === null) this.fetchData();
  }

  /**
   * @brief Carga las alarmas activas de la ubicación sin limitarse al día actual.
   *
   * El día que se representa se resuelve después a partir de la alarma más reciente,
   * por lo que una alarma antigua sigue pudiendo dibujarse en su período correcto.
   */
  fetchData(): void {
    this.loading = true;
    this.error = false;
    const filterOp: any = {
      alarm_status_id: 1,
      simple: true,
      location_id: this.locationId
    };

    this.subscription = this.alarmService.getPage({
      sortField: 'id',
      sortOrder: 'desc',
      pageSize: 0,
      pageNumber: 0,
      filter: filterOp
    } as any).pipe(
      map((response: any) => response && Array.isArray(response.items)
        ? response.items
        : response && Array.isArray(response.itemList) ? response.itemList : []),
      finalize(() => this.loading = false)
    ).subscribe((alarmList: any[]) => {
      this._alarmList = alarmList || [];
      this.updateAlarmList();
    }, () => {
      this.error = true;
      this.loaded = true;
      this.alarmChartReady = false;
    });
  }

  /**
   * @brief Construye el mismo gráfico temporal de alarmas usado por el modal.
   * @param {any[]} alarmList - Alarmas activas asociadas a la ubicación.
   */
  updateAlarmList(): void {
    try {
      const alarmList = this._alarmList || [];
      if (!alarmList.length) {
        this.resetAlarmChart();
        this.loaded = true;
        this.error = false;
        return;
      }

      const period = this.resolveAlarmDay(alarmList);
      if (!period) {
        this.resetAlarmChart();
        this.loaded = true;
        this.error = false;
        return;
      }

      const alarmGroups = new Map<string, number[]>();
      alarmList.forEach(alarm => {
        const date = this.getAlarmDate(alarm);
        if (!date || date < period.start || date > period.end) return;
        const sensorType = alarm?.SourceDeviceSensorType?.name || 'Sin información';
        const dates = alarmGroups.get(sensorType) || [];
        dates.push(date.getTime());
        alarmGroups.set(sensorType, dates);
      });

      this.alarmChartTypes = Array.from(alarmGroups.keys());
      if (!this.alarmChartTypes.length) {
        this.resetAlarmChart();
        this.loaded = true;
        this.error = false;
        return;
      }

      this.alarmChartData = this.alarmChartTypes.map(sensorType => {
        const eventTimes = Array.from(new Set(alarmGroups.get(sensorType) || [])).sort((a, b) => a - b);
        const points: Array<{x: Date; y: number}> = [{x: period.start, y: 0}];
        eventTimes.forEach(time => {
          const eventDate = new Date(time);
          const resetDate = new Date(Math.min(time + 60000, period.end.getTime()));
          points.push({x: eventDate, y: 0}, {x: eventDate, y: 1});
          if (resetDate.getTime() > eventDate.getTime()) points.push({x: resetDate, y: 0});
        });
        points.push({x: period.end, y: 0});
        points.sort((a, b) => a.x.getTime() - b.x.getTime() || a.y - b.y);
        return {
          data: points,
          label: sensorType,
          alarmTimes: eventTimes,
          borderWidth: 2,
          fill: false,
          spanGaps: true,
          pointRadius: 0,
          pointHoverRadius: 5,
          pointHitRadius: 12
        };
      });

      this.alarmChartColors = this.alarmChartTypes.map((_, index) => {
        const color = ALARM_SERIES_COLORS[index % ALARM_SERIES_COLORS.length];
        return {borderColor: color, backgroundColor: `${color}1a`, pointBackgroundColor: color, pointBorderColor: '#fff'};
      });
      this.alarmChartOptions = this.createAlarmChartOptions(period.start, period.end);
      this.alarmChartReady = true;
      this.loaded = true;
      this.error = false;
    } catch (err) {
      this.resetAlarmChart();
      this.error = true;
      this.loaded = true;
      console.error('alarm chart error:', err);
    }
  }

  /**
   * @brief Conserva el formato de datos simple para consumidores anteriores.
   * @param {any[]} alarmList - Alarmas activas asociadas a la ubicación.
   * @returns {any[]} Puntos de la primera serie.
   */
  generateAlarmData(alarmList: any[]): any[] {
    const alarm = (alarmList || []).slice().sort((a, b) =>
      (this.getAlarmDate(a)?.getTime() || 0) - (this.getAlarmDate(b)?.getTime() || 0)
    );
    const period = this.resolveAlarmDay(alarm);
    if (!period) return [];
    const sensorType = alarm.length ? alarm[0]?.SourceDeviceSensorType?.name || 'Sin información' : 'Sin información';
    const eventTimes = alarm.filter(row => {
      const date = this.getAlarmDate(row);
      return date && date >= period.start && date <= period.end
        && (row?.SourceDeviceSensorType?.name || 'Sin información') === sensorType;
    }).map(row => this.getAlarmDate(row).getTime());
    const points = [];
    const currentMinute = new Date(period.start);
    while (currentMinute.getTime() <= period.end.getTime()) {
      points.push({value: eventTimes.includes(currentMinute.getTime()) ? 1 : 0, label: this.formatTime(currentMinute)});
      currentMinute.setMinutes(currentMinute.getMinutes() + 1);
    }
    return points;
  }

  /**
   * @brief Resuelve el día de la alarma más reciente para el eje temporal.
   * @param {any[]} alarmList - Alarmas a evaluar.
   * @returns {{start: Date; end: Date} | null} Inicio y fin del día representado.
   */
  private resolveAlarmDay(alarmList: any[]): {start: Date; end: Date} | null {
    const dates = (alarmList || []).map(alarm => this.getAlarmDate(alarm)).filter(date => date !== null) as Date[];
    if (!dates.length) return null;
    const referenceDate = dates.reduce((latest, date) => date.getTime() > latest.getTime() ? date : latest);
    const start = new Date(referenceDate);
    const end = new Date(referenceDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return {start, end};
  }

  /**
   * @brief Configura el eje temporal, la leyenda y el tooltip del gráfico.
   * @param {Date} start - Inicio del día representado.
   * @param {Date} end - Fin del día representado.
   * @returns {any} Configuración compatible con Chart.js 2.
   */
  private createAlarmChartOptions(start: Date, end: Date): any {
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: {duration: 350, easing: 'easeOutQuart'},
      elements: {line: {tension: 0}, point: {radius: 0, hoverRadius: 5, hitRadius: 12}},
      tooltips: {
        enabled: true,
        mode: 'nearest',
        intersect: false,
        callbacks: {
          title: (items: any[]) => {
            const item = items && items.length ? items[0] : null;
            return item ? this.formatAlarmTooltipDate(item.xValue || item.xLabel) : '';
          },
          label: (item: any, data: any) => {
            const dataset = data && data.datasets ? data.datasets[item.datasetIndex] : null;
            const rawTime = item && item.xValue !== undefined && item.xValue !== null ? item.xValue : item && item.xLabel;
            const pointTime = rawTime instanceof Date ? rawTime.getTime() : new Date(rawTime).getTime();
            const isAlarmPoint = Number(item && item.yLabel) > 0 ||
              (dataset && Array.isArray(dataset.alarmTimes) && dataset.alarmTimes.some(time => Math.abs(time - pointTime) < 1000));
            return `${dataset && dataset.label ? dataset.label : 'Sin información'}: ${isAlarmPoint ? 'Alarma detectada' : 'Sin alarma'}`;
          }
        }
      },
      hover: {mode: 'nearest', intersect: false},
      scales: {
        xAxes: [{
          type: 'time',
          distribution: 'linear',
          gridLines: {color: 'rgba(148, 163, 184, .10)'},
          time: {
            min: start,
            max: end,
            unit: 'hour',
            stepSize: 2,
            displayFormats: {hour: 'HH:mm'},
            tooltipFormat: 'DD/MM/YYYY HH:mm:ss'
          },
          ticks: {fontColor: '#94a3b8', maxRotation: 0, minRotation: 0}
        }],
        yAxes: [{
          ticks: {min: 0, max: 1, stepSize: 1, fontColor: '#94a3b8', callback: (value: number) => String(value)},
          gridLines: {color: 'rgba(148, 163, 184, .16)'}
        }]
      },
      legend: {
        display: this.alarmChartTypes.length > 0,
        position: 'bottom',
        labels: {fontColor: '#cbd5e1', boxWidth: 12, padding: 10}
      }
    };
  }

  private getAlarmDate(alarm: any): Date | null {
    const raw = alarm?.create_date || alarm?.write_date;
    const date = raw ? new Date(raw) : null;
    return date && !isNaN(date.getTime()) ? date : null;
  }

  private formatAlarmTooltipDate(value: any): string {
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return '';
    const dateLabel = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
    const timeLabel = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
    return `${dateLabel} ${timeLabel}`;
  }

  private formatTime(date: Date): string {
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }

  private resetAlarmChart(): void {
    this.alarmChartData = [];
    this.alarmChartColors = [];
    this.alarmChartOptions = {};
    this.alarmChartTypes = [];
    this.alarmChartReady = false;
  }

  get chartHeight(): number {
    const height = Number(this.compactHeight);
    const compactChartHeight = Number.isFinite(height) ? Math.min(height, 150) : 150;
    return this.compact ? Math.max(100, compactChartHeight) : 350;
  }

  ngOnDestroy(): void {
    if (this.subscription) this.subscription.unsubscribe();
  }
}
